/* eslint-disable react/no-array-index-key */
import React from 'react';
import { Switch, Route, Router } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createBrowserHistory } from 'history';

import { AuthProvider } from './core/context/auth';
import { StoreProvider } from './core/context/store';
import { routes } from './modules';
import { initSentry } from './sentry';

// import spinkit styles
import 'spinkit/spinkit.min.css';
// Import Font Awesome Icons Set
import 'font-awesome/css/font-awesome.min.css';
// Import Simple Line Icons Set
import 'simple-line-icons/css/simple-line-icons.css';

import './assets/styles/style.scss';
import { ErrorBoundary } from './core/components/error-handling/global-error-boundary';
import { FeatureToggleStatus } from './core/components/feature-toggle-status';
import SignedCookieFetcher from './utils/signed-cookie-fetcher';
import { AppConfig } from './core/config';
import { AppEnvironments } from './core/constants';

const history = createBrowserHistory();
const queryClient = new QueryClient();
initSentry({ history, routes });

const App: React.FC = (): React.ReactElement => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ componentStack, error }) => <ErrorBoundary error={error} componentStack={componentStack} />}
    >
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools /> {/* only runs in development */}
          <SignedCookieFetcher>
            <StoreProvider initialState={{}}>
              <DndProvider backend={HTML5Backend}>
                <Router history={history}>
                  <Switch>
                    {routes.map((route, key) => {
                      return (
                        <Route
                          key={key}
                          path={route.path}
                          exact={route.exact}
                          sensitive={route.sensitive}
                          strict={route.strict}
                        >
                          {route.layout ? <route.layout route={route} /> : <route.page route={route} />}
                        </Route>
                      );
                    })}
                  </Switch>
                </Router>
              </DndProvider>
              <div id="feature-toggle-status-root">
                {AppConfig.env !== AppEnvironments.PRODUCTION && <FeatureToggleStatus />}
              </div>
            </StoreProvider>
          </SignedCookieFetcher>
        </QueryClientProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
